/**
 * Pure helpers for what gets persisted into message_metadata on an
 * assistant message. Extracted from use-chat.ts's onFinish handler so they
 * can be unit-tested without standing up the full hook (Incident B
 * audit-trail fix, 2026-07-08 — previously message_metadata.tool_results
 * captured 0 of update_odm_xml/validate_3ph_odm's actual args/results,
 * only add_to_canvas/show_form_preview).
 */
import type { ToolResult } from './crucible-tools'
import type { ExecutedTool } from '@/types/chat'
import type { ToolId } from '@/lib/ai/prompts/canvas-meta'

export type PersistableTool =
  | { tool: 'add_to_canvas'; title: string; content: string; sectionId?: string; targetTool?: ToolId }
  | { tool: 'show_form_preview'; formXml: string; formName: string }
  | { tool: 'update_odm_xml'; content: string; isFragment: boolean; title: string }

/**
 * Which executed UI-tool results are worth persisting for session-reload
 * canvas reconstruction and forensic audit trail. update_odm_xml joined
 * add_to_canvas/show_form_preview here (Incident B) — its results were
 * already flowing through pendingToolResultsRef, just filtered out here.
 */
export function buildPersistableTools(
  executedTools: ToolResult[],
  /**
   * Canvas that received the content — the persona active when the tool ran.
   * Recorded so later reads stop inferring it from whichever workspace happens
   * to be open. See @/lib/ai/canvas-ownership.
   */
  canvasOwner?: ToolId,
): PersistableTool[] {
  return executedTools
    .filter(
      (t): t is Extract<ToolResult, { tool: 'add_to_canvas' | 'show_form_preview' | 'update_odm_xml' }> =>
        t.tool === 'add_to_canvas' || t.tool === 'show_form_preview' || t.tool === 'update_odm_xml'
    )
    .map((t): PersistableTool => {
      if (t.tool === 'add_to_canvas') {
        return {
          tool: t.tool,
          title: t.title,
          content: t.content,
          sectionId: t.sectionId,
          ...(canvasOwner ? { targetTool: canvasOwner } : {}),
        }
      }
      if (t.tool === 'update_odm_xml') {
        return { tool: t.tool, content: t.content, isFragment: t.isFragment, title: t.title }
      }
      return { tool: t.tool, formXml: t.formXml, formName: t.formName }
    })
}

/**
 * Per-string cap inside persisted tool args. The BFF's log line uses 240,
 * which cuts most real SQL mid-statement; 2000 captures a stride_query or
 * survey_query whole while still bounding a large add_to_canvas payload
 * (whose full content is already persisted by buildPersistableTools above,
 * so storing it twice buys nothing).
 */
export const ARG_VALUE_MAX_CHARS = 2000

/** Marker appended to a cut value, so a reader never mistakes a truncated
 *  record for the whole call. */
export const ARGS_TRUNCATED_SUFFIX = '…[truncated]'

/**
 * Bound an args object for persistence by truncating oversized string values,
 * preserving the object shape.
 *
 * Shape matters: llm_ledger.py stores this in tool_calls.args, a jsonb column,
 * and an object stays queryable there (args->>'query'). Flattening to a string
 * would make the audit trail unsearchable, which defeats the purpose.
 */
export function capToolArgs(input: unknown): Record<string, unknown> | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined
  const capValue = (v: unknown): unknown => {
    if (typeof v === 'string' && v.length > ARG_VALUE_MAX_CHARS) {
      return v.slice(0, ARG_VALUE_MAX_CHARS) + ARGS_TRUNCATED_SUFFIX
    }
    if (Array.isArray(v)) return v.map(capValue)
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, val]) => [k, capValue(val)]),
      )
    }
    return v
  }
  const out = Object.fromEntries(
    Object.entries(input as Record<string, unknown>).map(([k, v]) => [k, capValue(v)]),
  )
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Folds a tracked tool execution into its tools_used persistence shape.
 *
 * `details` is only present for tools the backend enriches beyond a bare
 * pass/fail status (currently just validate_3ph_odm — see the
 * data-crucible-tool-status handler in use-chat.ts).
 *
 * `input` closes the audit gap found 2026-09-19. llm_ledger.py's
 * _normalize_tools_used has always read `entry["input"]` to populate
 * tool_calls.args — but this function dropped the field, so args was NULL for
 * all 9,270 rows ever written, across every tool. Nothing recorded what any
 * tool was asked to DO, only that it ran and passed. That included
 * stride_query, which authors arbitrary SQL against the clinical database.
 */
export function buildToolsUsedEntry(t: ExecutedTool): ExecutedTool {
  const entry: ExecutedTool = { name: t.name, status: t.status, timestamp: t.timestamp }
  const input = capToolArgs(t.input)
  if (input) entry.input = input
  if (t.details) entry.details = t.details
  return entry
}

/**
 * Maps a backend data-crucible-tool-status event's `status` field to the
 * ExecutedTool status. Every data-tool dispatcher (qualio_service,
 * web_fetch_service, etc.) catches its own failures and returns an
 * "ERROR..." string rather than raising, so the backend's tool-status event
 * used to always say "complete" — a genuine Qualio API 500 was
 * indistinguishable from a real result, both in the UI and in the
 * tool_calls observability ledger downstream. The backend now emits
 * status: 'error' for these (see azure_ai.py's _is_tool_error_result);
 * anything else (including an absent status, e.g. UI tools that don't set
 * one) is a real success (2026-07-28).
 */
export function toExecutedToolStatus(backendStatus: string | undefined): 'success' | 'error' {
  return backendStatus === 'error' ? 'error' : 'success'
}
